import {
  namedToOptionError,
  displayNameToOptionError,
  namedToOptionWithError,
} from 'services/helpers';
import {
  checkDeleted,
  checkListDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import { type StartAutomationStep } from '../actionSectionTypes';
import type {
  AutomationVariableOption,
  FromApiOveriderVariables,
  FromApiVariableOption,
  OveriderVariableData,
  RecordSource,
  SpecificValueOverride,
  ValueSourceType,
} from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { RECORD_SOURCES } from './constants';
import type { Field, GenericOption } from 'pages/AutomationEngine/types';

export const displayNameToOption = ({
  id,
  displayName,
  ...rest
}: GenericOption & any) => ({
  value: id,
  label: displayName,
  ...rest,
});

const dataSubtypeForOption = (
  dataSubtype: FromApiVariableOption['dataSubtype']
) =>
  dataSubtype
    ? {
        ...dataSubtype,
        label:
          dataSubtype?.description ||
          dataSubtype?.objectName ||
          dataSubtype?.label,
        value: dataSubtype?.value || dataSubtype?.id,
        entityName: dataSubtype?.entityName,
        objectName: dataSubtype?.objectName,
      }
    : undefined;

const variableToOption = ({
  name,
  deleted = false,
  dataType,
  dataSubtype,
  isArray,
}: FromApiVariableOption): AutomationVariableOption => ({
  value: name,
  label: name,
  deleted,
  meta: {
    dataType,
    dataSubtype: dataSubtypeForOption(dataSubtype),
    sourceType: 'static',
    isArray,
  },
  isVariable: true,
});

type ApiOveriderVariableData = {
  variableToOverride: string;
  variableToOverrideName: string | null;
  variableToOverrideIsDeleted: boolean;
  valueSource?: ValueSourceType;
  // specific_value
  specificValue?: string | string[] | null;
  // context_entity_field
  contextEntityField?: string | null; // field on ctx automation saved as an uuid loaded as a field
  // automation_variable
  automationVariable?: string | null; // (parent) saved as variableName returned as variable Object
  // related object
  relationshipField?: string | null; // field on ctx automation saved as uuid load as a field
  relatedRecordField?: string | null; // field on related object saved as uuid load as a field
  // entity_variable_field
  automationEntityVariable?: string | { name: string | undefined } | null; // (Parent ) saved as variableName returned as variable Object
  automationEntityVariableField?: string | null; // field on related object saved as uuid loaded as a field
};

const forApi = (step: StartAutomationStep) => {
  const details = { ...step.details };
  const {
    recordSource,
    relationshipFields,
    automationVariable,
    automations,
    resumePausedAutomations,
    automationVariableOverrides: _automationVariableOverrides,
  } = details;

  const automationVariableOverrides = _automationVariableOverrides.map(
    (variable) => {
      const variableOverrides = variable.variableOverrides.reduce(
        (prev, override) => {
          const {
            specificValue,
            contextEntityField,
            automationVariable,
            relationshipField,
            relatedRecordField,
            automationEntityVariable,
            automationEntityVariableField,

            ...nextOverride
          } = override;

          switch (override.valueSource) {
            case 'context_entity_field': {
              prev.push({
                ...nextOverride,
                contextEntityField: contextEntityField?.value,
              });
              break;
            }

            case 'related_object_field': {
              prev.push({
                ...nextOverride,
                relationshipField: relationshipField?.value,
                relatedRecordField: relatedRecordField?.value,
              });
              break;
            }

            case 'automation_variable': {
              prev.push({
                ...nextOverride,
                automationVariable: override.automationVariable?.value,
              });
              break;
            }

            case 'automation_entity_variable_field': {
              prev.push({
                ...nextOverride,
                automationEntityVariable:
                  override.automationEntityVariable?.value,

                automationEntityVariableField:
                  automationEntityVariableField?.value,
              });
              break;
            }

            case 'specific_value': {
              prev.push({
                ...nextOverride,
                specificValue: Array.isArray(specificValue)
                  ? specificValue.map((v): string =>
                      typeof v === 'object' ? v?.value : v
                    )
                  : specificValue && typeof specificValue === 'object'
                    ? specificValue?.value
                    : specificValue,
              });
              break;
            }

            case 'blank': {
              prev.push(nextOverride);
              break;
            }

            default: // none we skip
              break;
          }

          return prev;
        },
        [] as Array<ApiOveriderVariableData>
      );

      return { ...variable, variableOverrides };
    }
  );

  const api = {
    details: {
      recordSource,
      relationshipFieldIds: relationshipFields.map(({ value }) => value),
      automationVariableName: automationVariable?.value || null,
      resumePausedAutomations,
      automationIds: automations.map(({ value }) => value),
      automationVariableOverrides,
    },
  };

  return api;
};

type ApiObject = {
  recordSource: RecordSource;
  relationshipFields: (Field & { deleted: boolean })[];
  automationVariable: {
    name: string;
    deleted: boolean;
    dataSubtype: string;
  } | null;
  resumePausedAutomations: boolean;
  automations: { id: string; name: string; deleted: boolean }[];
  automationVariableOverrides: FromApiOveriderVariables[];
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  let variableOverrideErrors = {};
  const {
    recordSource,
    relationshipFields,
    automationVariable,
    resumePausedAutomations,
    automations,
    automationVariableOverrides: _automationVariableOverrides = [],
  } = apiObject;

  if (recordSource === RECORD_SOURCES.AUTOMATION_VARIABLE) {
    checkDeleted(
      messageDictionary,
      addErrorMessage,
      automationVariable,
      nameDescriptor,
      undefined,
      'automationVariable'
    );
  }

  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    checkListDeleted(
      messageDictionary,
      addErrorMessage,
      relationshipFields,
      displayNameDescriptor,
      messageDictionary.relatedObjectField,
      'relationshipFields',
      displayNameToOptionError,
      true
    );
  }

  checkListDeleted(
    messageDictionary,
    addErrorMessage,
    automations,
    nameDescriptor,
    messageDictionary.automation,
    'automations',
    namedToOptionError,
    true
  );

  let relatedObject = null;
  if (recordSource === RECORD_SOURCES.RELATED_OBJECT_FIELD) {
    const firstField = relationshipFields[0];
    if (firstField) {
      const { relation } = firstField;
      relatedObject = {
        value: relation.relatedObject,
        label: relation.relatedObjectName,
        deleted: false,
        entityName: relation.relatedEntityName,
        relation,
      };
    }
  }

  const automationVariableOverrides = automations.map((a) => {
    let variableError: Record<string, any> | undefined = undefined;

    const addError = (id: string, errorField: string) => {
      if (!variableError) {
        variableError = {};
      }

      variableError = {
        ...variableError,
        [id]: {
          [errorField]: 'deleted',
        },
      };
    };

    let deleteCounter = 1;

    const variableOverrides = _automationVariableOverrides
      .filter(({ targetAutomation }) => targetAutomation.id === a.id)
      .reduce((prev, variable) => {
        const { variableToOverride } = variable;

        let variableToOverrideName = variableToOverride.name;

        if (variableToOverride.deleted) {
          variableToOverrideName = variableToOverride.name
            ? `${variableToOverride.name}`
            : `deleted_${deleteCounter}`;
          deleteCounter += 1;

          addError(variableToOverrideName, 'target_deleted');
          addErrorMessage(
            `${messageDictionary.deletedOverrideVariable} ${variableToOverrideName}`
          );
        }

        const override: OveriderVariableData = {
          variableToOverride: variableToOverrideName,
          variableToOverrideName: variableToOverrideName,
          variableToOverrideIsDeleted: variableToOverride.deleted,
          valueSource: variable.valueSource,
          specificValue: null,
          contextEntityField: null,
          automationVariable: null,
          relationshipField: null,
          relatedRecordField: null,
          automationEntityVariable: null,
          automationEntityVariableField: null,
          isRequired: false,
        };

        switch (variable.valueSource) {
          case 'specific_value': {
            const specificValue = variable.specificValue;

            prev.push({
              ...override,
              specificValue: (Array.isArray(specificValue)
                ? specificValue.map((v) =>
                    v && typeof v === 'object'
                      ? {
                          value: v.id,
                          label: v.displayName,
                          deleted: v.deleted,
                        }
                      : v
                  )
                : specificValue && typeof specificValue === 'object'
                  ? {
                      value: specificValue.id,
                      label: specificValue.displayName,
                      deleted: specificValue.deleted,
                    }
                  : specificValue) as SpecificValueOverride,
            });

            break;
          }

          case 'context_entity_field': {
            if (variable?.contextEntityField?.deleted) {
              addError(override.variableToOverride, 'contextEntityField');
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.contextEntityField?.displayName}'`
              );
            }

            prev.push({
              ...override,
              contextEntityField: displayNameToOption(
                variable.contextEntityField!
              ),
            });
            break;
          }

          case 'related_object_field': {
            if (variable?.relationshipField?.deleted) {
              addError(override.variableToOverride, 'relationshipField');
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.relationshipField.displayName}'`
              );
            }
            if (variable?.relatedRecordField?.deleted) {
              addError(override.variableToOverride, 'relatedRecordField');
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.relatedRecordField.displayName}'`
              );
            }

            prev.push({
              ...override,
              relationshipField: displayNameToOption(
                variable?.relationshipField!
              ),
              relatedRecordField: displayNameToOption(
                variable?.relatedRecordField!
              ),
            });
            break;
          }

          case 'automation_variable': {
            if (variable?.automationVariable?.deleted) {
              addError(override.variableToOverride, 'automationVariable');
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.automationVariable.name}'`
              );
            }
            prev.push({
              ...override,
              automationVariable: variable?.automationVariable
                ? variableToOption(variable?.automationVariable)
                : null,
            });
            break;
          }

          case 'automation_entity_variable_field': {
            if (variable?.automationEntityVariable?.deleted) {
              addError(override.variableToOverride, 'automationEntityVariable');
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.automationEntityVariable.name}'`
              );
            }
            if (variable?.automationEntityVariableField?.deleted) {
              addError(
                override.variableToOverride,
                'automationEntityVariableField'
              );
              addErrorMessage(
                `${messageDictionary.thereWasanErrorLoadingThe} '${variable?.automationEntityVariableField.displayName}'`
              );
            }
            prev.push({
              ...override,
              automationEntityVariable: variable?.automationEntityVariable
                ? variableToOption(variable?.automationEntityVariable)
                : null,
              automationEntityVariableField: displayNameToOption(
                variable?.automationEntityVariableField!
              ),
            });

            break;
          }

          case 'blank': {
            prev.push(override);
            break;
          }
        }

        if (variableError) {
          variableOverrideErrors = {
            ...variableOverrideErrors,
            [variable.targetAutomation.id]: variableError,
          };
        }

        return prev;
      }, [] as Array<OveriderVariableData>);

    return {
      automationId: a.id,
      automationName: a.name,
      variableOverrides,
      automationDeleted: a.deleted,
    };
  });

  return {
    recordSource,
    relatedObject,
    relationshipFields: (relationshipFields || []).map((field) => ({
      value: field.id,
      label: field.displayName,
      deleted: field.deleted,
      error: field.deleted,
      data: field,
    })),
    automationVariable: automationVariable && {
      value: automationVariable.name,
      label: automationVariable.name,
      deleted: automationVariable.deleted,
      meta: { dataSubtype: { value: automationVariable.dataSubtype } },
    },
    resumePausedAutomations,
    automations: (automations || []).map(namedToOptionWithError),
    automationVariableOverrides,
    variableOverrideErrors,
  };
};

export const startAutomationDTO = {
  forApi,
  forFlow,
};
